import type { SessionInboxInfo } from '@opencode/client/promise';

export type SteerCutInTarget = {
  waiting: () => Promise<readonly SessionInboxInfo[]>;
  interruptAndResume: () => Promise<unknown>;
  wakeQueue: (waiting: readonly SessionInboxInfo[]) => Promise<void>;
};

/**
 * OpenCode delivers a steer at the agent's next step, which can be a command that takes minutes
 * or a question that never ends on its own. An interrupt with `resume` ends the step and delivers
 * the waiting steers. After it, OpenCode wakes for steers only, so queued prompts stay parked
 * until something wakes it, one queued after the inbox read included. The interrupt also drops
 * any wake asked for before it answered, so the queue's wake comes after the interrupt answers.
 */
export async function cutInForWaitingSteers(target: SteerCutInTarget): Promise<boolean> {
  // With no steer waiting, the interrupt would only stop the agent. A steer
  // delivered after this read is interrupted in its own reply.
  const waiting = await target.waiting();
  if (!waiting.some((prompt) => prompt.delivery === 'steer')) {
    return false;
  }
  await target.interruptAndResume();
  // The interrupt happened either way: a queue that could not be woken waits for the next prompt.
  await target.wakeQueue(waiting).catch(() => undefined);
  return true;
}
